import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { projectApiService, Project } from '../../recruiter/services/projectApi';
import { Job } from '../../recruiter/services/jobApi';
import { COMPANY_ROUTES } from '../constants';
import { MoreVertical, FolderInput, Trash2, X, Folder, Users, DollarSign, Clock, Briefcase } from 'lucide-react';
import JobPostingMethodModal from '../components/JobPostingMethodModal';

/**
 * Project Details Page
 *
 * Shows all jobs within a specific project
 * User can post new jobs to this project
 */
const ProjectDetails: React.FC = () => {
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId: string }>();
    const [projectName, setProjectName] = useState('');
    const [_companyName, _setCompanyName] = useState('');
    const [jobs, setJobs] = useState<Job[]>([]);
    const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [_searchQuery, _setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [_totalPages, _setTotalPages] = useState(1);
    const jobsPerPage = 10;

    // Action menu state
    const [activeMenuJobId, setActiveMenuJobId] = useState<string | null>(null);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [selectedJobTitle, setSelectedJobTitle] = useState<string>('');
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showPostJobModal, setShowPostJobModal] = useState(false);

    useEffect(() => {
        if (projectId) {
            fetchProjectJobs();
        }
    }, [projectId, currentPage]);

    useEffect(() => {
        if (_searchQuery.trim() === '') {
            setFilteredJobs(jobs);
        } else {
            const query = _searchQuery.toLowerCase();
            const filtered = jobs.filter((job) => {
                const title = (job.title || '').toLowerCase();
                const empType = (job.employmentType || '').toLowerCase();
                return title.includes(query) || empType.includes(query);
            });
            setFilteredJobs(filtered);
            setCurrentPage(1);
        }
    }, [_searchQuery, jobs]);

    const fetchProjectJobs = async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            setError(null);
            const response = await projectApiService.getProjectJobs(projectId, currentPage, jobsPerPage);
            setProjectName(response.project.name);
            _setCompanyName(response.project.companyName);
            setJobs(response.jobs);
            setFilteredJobs(response.jobs);
            _setTotalPages(response.pagination.totalPages);
        } catch (err) {
            console.error('Failed to fetch project jobs:', err);
            setError('Failed to load project jobs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handlePostJob = () => {
        setShowPostJobModal(true);
    };

    const handleJobClick = (jobId: string) => {
        navigate(COMPANY_ROUTES.JOB_DETAILS(jobId));
    };

    const getStatusBadge = (job: Job) => {
        if (job.isActive === false) {
            return { border: 'border-red-500', text: 'text-red-500', label: 'Closed' };
        }
        return { border: 'border-[rgba(4,218,0,0.91)]', text: 'text-[rgba(4,218,0,0.91)]', label: 'Active' };
    };

    const getSalaryRange = (job: Job) => {
        return job.salaryRange || 'Not specified';
    };

    // Fetch all projects for the move modal
    const fetchProjects = async () => {
        try {
            setIsLoadingProjects(true);
            const response = await projectApiService.listProjects(1, 100);
            // Filter out current project
            setProjects((response.data || []).filter(p =>
                (p.projectId || p._id) !== projectId
            ));
        } catch (err) {
            console.error('Failed to fetch projects:', err);
        } finally {
            setIsLoadingProjects(false);
        }
    };

    // Open move modal
    const handleOpenMoveModal = (jobId: string, jobTitle: string) => {
        setSelectedJobId(jobId);
        setSelectedJobTitle(jobTitle);
        setActiveMenuJobId(null);
        fetchProjects();
        setShowMoveModal(true);
    };

    // Move job to another project
    const handleMoveJob = async (targetProjectId: string) => {
        if (!selectedJobId || !projectId) return;

        try {
            setIsMoving(true);
            // Remove from current project
            await projectApiService.removeJobFromProject(projectId, selectedJobId);
            // Add to target project
            await projectApiService.addJobToProject(targetProjectId, selectedJobId);
            // Refresh the job list
            await fetchProjectJobs();
            setShowMoveModal(false);
            setSelectedJobId(null);
        } catch (err) {
            console.error('Failed to move job:', err);
            alert('Failed to move job. Please try again.');
        } finally {
            setIsMoving(false);
        }
    };

    // Open delete confirmation modal
    const handleOpenDeleteModal = (jobId: string, jobTitle: string) => {
        setSelectedJobId(jobId);
        setSelectedJobTitle(jobTitle);
        setActiveMenuJobId(null);
        setShowDeleteModal(true);
    };

    // Delete job (remove from project)
    const handleDeleteJob = async () => {
        if (!selectedJobId || !projectId) return;

        try {
            setIsDeleting(true);
            // Remove job from this project
            await projectApiService.removeJobFromProject(projectId, selectedJobId);
            // Refresh the job list
            await fetchProjectJobs();
            setShowDeleteModal(false);
            setSelectedJobId(null);
        } catch (err) {
            console.error('Failed to remove job:', err);
            alert('Failed to remove job from folder. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveMenuJobId(null);
        if (activeMenuJobId) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [activeMenuJobId]);

    return (
        <CompanyLayout>
            <div style={{ padding: '24px', background: '#F1F3F4', fontFamily: 'Manrope, sans-serif' }}>
                {/* Main Container */}
                <div style={{
                    background: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '24px'
                }}>
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/companies/projects')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            marginBottom: '16px',
                            padding: '4px',
                            color: '#015FA6'
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="#015FA6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '19px',
                            color: '#015FA6'
                        }}>
                            Back to Projects
                        </span>
                    </button>

                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                    }}>
                        <h1 style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '24px',
                            lineHeight: '33px',
                            color: '#0B111E',
                            margin: 0
                        }}>
                            {projectName || 'Recently Posted Jobs'}
                        </h1>

                        <button
                            onClick={handlePostJob}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '8px 16px',
                                gap: '8px',
                                background: '#015FA6',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                height: '40px'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="#FFFFFF"/>
                            </svg>
                            <span style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: '22px',
                                color: '#FFFFFF'
                            }}>
                                Post new job
                            </span>
                        </button>
                    </div>

                    {/* Table Header */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 100px 140px 140px 200px',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: '#F8F9FB',
                        borderRadius: '12px',
                        marginBottom: '8px',
                        gap: '16px'
                    }}>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#757575',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Job Title
                        </span>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#757575',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                        }}>
                            Status
                        </span>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#757575',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                        }}>
                            Applications
                        </span>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#757575',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                        }}>
                            Salary
                        </span>
                        <span style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#757575',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                        }}>
                            Actions
                        </span>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">Loading jobs...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="text-center py-12">
                            <p className="text-red-500">{error}</p>
                            <button
                                onClick={fetchProjectJobs}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Try Again
                            </button>
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && !error && filteredJobs.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">
                                No jobs in this project yet. Post your first job to get started!
                            </p>
                        </div>
                    )}

                    {/* Jobs List */}
                    {!loading && !error && filteredJobs.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                        }}>
                            {filteredJobs.map((job) => {
                                const status = getStatusBadge(job);
                                const jobId = job.id || job._id;
                                const title = job.title;
                                const empType = job.employmentType;
                                return (
                                    <div
                                        key={jobId}
                                        onClick={() => handleJobClick(jobId || '')}
                                        className="group"
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 100px 140px 140px 200px',
                                            alignItems: 'center',
                                            padding: '16px',
                                            background: '#FFFFFF',
                                            borderRadius: '12px',
                                            border: '1px solid #EDEDED',
                                            cursor: 'pointer',
                                            gap: '16px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#015FA6';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(1, 95, 166, 0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#EDEDED';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        {/* Job Title Section */}
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '6px',
                                            minWidth: 0
                                        }}>
                                            <span style={{
                                                fontFamily: 'Manrope',
                                                fontWeight: 600,
                                                fontSize: '15px',
                                                lineHeight: '20px',
                                                color: '#0B111E',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {title}
                                            </span>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: '12px',
                                                flexWrap: 'wrap'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Briefcase size={12} color="#757575" />
                                                    <span style={{
                                                        fontFamily: 'Manrope',
                                                        fontWeight: 400,
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        color: '#757575'
                                                    }}>
                                                        {empType || 'Full-time'}
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Clock size={12} color="#757575" />
                                                    <span style={{
                                                        fontFamily: 'Manrope',
                                                        fontWeight: 400,
                                                        fontSize: '12px',
                                                        lineHeight: '16px',
                                                        color: '#757575'
                                                    }}>
                                                        27 days left
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Badge */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}>
                                            <div style={{
                                                display: 'inline-flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                padding: '4px 12px',
                                                background: status.label === 'Active' ? 'rgba(4, 218, 0, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                borderRadius: '20px'
                                            }}>
                                                <span style={{
                                                    fontFamily: 'Manrope',
                                                    fontWeight: 500,
                                                    fontSize: '12px',
                                                    lineHeight: '16px',
                                                    color: status.label === 'Active' ? '#16A34A' : '#DC2626'
                                                }}>
                                                    {status.label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Applications */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            <Users size={16} color="#015FA6" />
                                            <span style={{
                                                fontFamily: 'Manrope',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                lineHeight: '19px',
                                                color: '#0B111E'
                                            }}>
                                                {(job as any).applicationCount || 0}
                                            </span>
                                        </div>

                                        {/* Salary */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <DollarSign size={14} color="#757575" />
                                            <span style={{
                                                fontFamily: 'Manrope',
                                                fontWeight: 400,
                                                fontSize: '13px',
                                                lineHeight: '18px',
                                                color: '#515151',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {getSalaryRange(job)}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/companies/applications-review');
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    padding: '8px 14px',
                                                    background: 'transparent',
                                                    border: '1px solid #015FA6',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    height: '34px',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = '#F4F7FE';
                                                    e.currentTarget.style.borderColor = '#014A82';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.borderColor = '#015FA6';
                                                }}
                                            >
                                                <span style={{
                                                    fontFamily: 'Manrope',
                                                    fontWeight: 500,
                                                    fontSize: '13px',
                                                    color: '#015FA6'
                                                }}>
                                                    View Candidates
                                                </span>
                                            </button>

                                            {/* Actions Dropdown */}
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveMenuJobId(activeMenuJobId === jobId ? null : jobId || null);
                                                    }}
                                                    style={{
                                                        width: '34px',
                                                        height: '34px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #EDEDED',
                                                        background: activeMenuJobId === jobId ? '#F4F7FE' : '#FFFFFF',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (activeMenuJobId !== jobId) {
                                                            e.currentTarget.style.background = '#F8F9FB';
                                                            e.currentTarget.style.borderColor = '#015FA6';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (activeMenuJobId !== jobId) {
                                                            e.currentTarget.style.background = '#FFFFFF';
                                                            e.currentTarget.style.borderColor = '#EDEDED';
                                                        }
                                                    }}
                                                >
                                                    <MoreVertical size={18} color="#515151" />
                                                </button>

                                                {/* Dropdown Menu */}
                                                {activeMenuJobId === jobId && (
                                                    <div
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            right: 0,
                                                            marginTop: '4px',
                                                            background: '#FFFFFF',
                                                            borderRadius: '12px',
                                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                                                            border: '1px solid #EDEDED',
                                                            minWidth: '180px',
                                                            zIndex: 100,
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <button
                                                            onClick={() => handleOpenMoveModal(jobId || '', title || '')}
                                                            style={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '12px',
                                                                padding: '12px 16px',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                textAlign: 'left',
                                                                fontFamily: 'Manrope',
                                                                fontSize: '14px',
                                                                color: '#0B111E',
                                                                transition: 'background 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#F4F7FE'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            <FolderInput size={18} color="#015FA6" />
                                                            <span>Move to folder</span>
                                                        </button>
                                                        <div style={{ height: '1px', background: '#EDEDED' }} />
                                                        <button
                                                            onClick={() => handleOpenDeleteModal(jobId || '', title || '')}
                                                            style={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '12px',
                                                                padding: '12px 16px',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                textAlign: 'left',
                                                                fontFamily: 'Manrope',
                                                                fontSize: '14px',
                                                                color: '#DC2626',
                                                                transition: 'background 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            <Trash2 size={18} color="#DC2626" />
                                                            <span>Remove from folder</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Move to Folder Modal */}
            {showMoveModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowMoveModal(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#FFFFFF',
                            borderRadius: '16px',
                            padding: '24px',
                            width: '100%',
                            maxWidth: '420px',
                            margin: '0 16px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                        }}>
                            <h3 style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                color: '#0B111E',
                                margin: 0
                            }}>
                                Move to Folder
                            </h3>
                            <button
                                onClick={() => setShowMoveModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px'
                                }}
                            >
                                <X size={20} color="#757575" />
                            </button>
                        </div>
                        <p style={{
                            fontFamily: 'Manrope',
                            fontSize: '14px',
                            color: '#757575',
                            marginBottom: '20px'
                        }}>
                            Move "{selectedJobTitle}" to another folder
                        </p>

                        <div style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            marginBottom: '16px'
                        }}>
                            {isLoadingProjects ? (
                                <div style={{ textAlign: 'center', padding: '24px', color: '#757575' }}>
                                    Loading folders...
                                </div>
                            ) : projects.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '24px', color: '#757575' }}>
                                    No other folders available
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {projects.map((project) => (
                                        <button
                                            key={project._id || project.projectId}
                                            onClick={() => handleMoveJob(project.projectId || project._id || '')}
                                            disabled={isMoving}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '12px 16px',
                                                background: '#FFFFFF',
                                                border: '1px solid #EDEDED',
                                                borderRadius: '8px',
                                                cursor: isMoving ? 'not-allowed' : 'pointer',
                                                opacity: isMoving ? 0.6 : 1,
                                                textAlign: 'left',
                                                fontFamily: 'Manrope',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isMoving) {
                                                    e.currentTarget.style.borderColor = '#015FA6';
                                                    e.currentTarget.style.background = '#F4F7FE';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = '#EDEDED';
                                                e.currentTarget.style.background = '#FFFFFF';
                                            }}
                                        >
                                            <Folder size={20} color="#015FA6" />
                                            <div>
                                                <p style={{
                                                    margin: 0,
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    color: '#0B111E'
                                                }}>
                                                    {project.name}
                                                </p>
                                                <p style={{
                                                    margin: 0,
                                                    fontSize: '12px',
                                                    color: '#757575'
                                                }}>
                                                    {project.jobIds?.length || 0} jobs
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setShowMoveModal(false)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'transparent',
                                border: '1px solid #A5A5A5',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#0B111E'
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <JobPostingMethodModal
                isOpen={showPostJobModal}
                onClose={() => setShowPostJobModal(false)}
                projectId={projectId}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowDeleteModal(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#FFFFFF',
                            borderRadius: '16px',
                            padding: '24px',
                            width: '100%',
                            maxWidth: '420px',
                            margin: '0 16px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                        }}>
                            <h3 style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                color: '#0B111E',
                                margin: 0
                            }}>
                                Remove from Folder
                            </h3>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px'
                                }}
                            >
                                <X size={20} color="#757575" />
                            </button>
                        </div>
                        <p style={{
                            fontFamily: 'Manrope',
                            fontSize: '14px',
                            color: '#757575',
                            marginBottom: '24px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to remove "{selectedJobTitle}" from this folder?
                            The job will still exist but won't be linked to any folder.
                        </p>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'transparent',
                                    border: '1px solid #A5A5A5',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: '#0B111E'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteJob}
                                disabled={isDeleting}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: '#DC2626',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: '#FFFFFF',
                                    opacity: isDeleting ? 0.6 : 1
                                }}
                            >
                                {isDeleting ? 'Removing...' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CompanyLayout>
    );
};

export default ProjectDetails;
